import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ReportColumnDef, ReportDef, ReportFilterDef } from '@project/contracts'
import { Button, Empty, Icon, Lookup, Modal, Money, Notice, Panel, Table, type ColumnDef } from '../design/index.js'
import { useApi, type DrillRow } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'
import { asText, formatDate, formatQuantity, formatRate } from './format.js'
import { DATE_PRESETS, answersRequired, defaultFilterValues } from './filters.js'
import { ColumnsButton, ExportButton, useChosenColumns } from './columns.js'
import { SavedViews } from './SavedViews.js'

const useReportDefs = () => {
  const api = useApi()
  return useQuery({ queryKey: ['reportDefs'], queryFn: () => api.reportMenu(), staleTime: 10 * 60_000 })
}

/** The Reports menu, in the six groups docs/10 uses. */
export const ReportsHub = ({ companyId }: { companyId: string }) => {
  const defs = useReportDefs()
  const groups = useMemo(() => {
    const byGroup = new Map<string, ReportDef[]>()
    for (const d of defs.data ?? []) {
      const list = byGroup.get(d.group)
      if (list) list.push(d)
      else byGroup.set(d.group, [d])
    }
    return [...byGroup.entries()]
  }, [defs.data])

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>

  return (
    <div className="page">
      <h1 className="page-title">Reports</h1>
      <p className="page-subtitle">Nothing runs until you set the filters — these read across everything.</p>
      {/* Not a report, but this is where the incumbent files it and where
          people look for it (docs/05 §2). */}
      <Panel title="Files">
        <div className="tile-grid">
          <div className="tile">
            <div className="tile__body">
              <Link to={paths.drive(companyId)} className="tile__title">Project Drive</Link>
              <p className="tile__desc">
                Every file on a job — drawings, certificates, the bills that came with a delivery.
              </p>
            </div>
          </div>
        </div>
      </Panel>
      {groups.map(([group, items]) => (
        <Panel key={group} title={group} aside={`${items.length} report${items.length === 1 ? '' : 's'}`}>
          <div className="tile-grid">
            {items.map((d) => (
              <div className="tile" key={d.key}>
                <div className="tile__body">
                  <Link to={paths.report(companyId, d.route)} className="tile__title">{d.label}</Link>
                  <p className="tile__desc">{d.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}

const cell = (col: ReportColumnDef, value: unknown) => {
  if (value === null || value === undefined || value === '') return null
  const s = String(value)
  switch (col.type) {
    case 'money': {
      const negative = Number(s) < 0
      return (
        <span style={col.signed ? { color: negative ? 'var(--bad)' : 'var(--good)' } : undefined}>
          <Money value={s} />
        </span>
      )
    }
    case 'qty': return <span className="num">{formatQuantity(s)}</span>
    case 'rate': return <span className="num">{formatRate(s)}</span>
    case 'pct': return <span className="num">{Number(s).toFixed(2)}%</span>
    case 'date': return <span className="num">{formatDate(s)}</span>
    case 'docno': return <span className="mono">{s}</span>
    default: return asText(value)
  }
}

/** One filter control, from the report's own definition. */
const FilterControl = ({ filter, value, onChange }: {
  filter: ReportFilterDef; value: Record<string, string>; onChange: (patch: Record<string, string>) => void
}) => {
  const id = `rf-${filter.key}`
  if (filter.control === 'dateRange') {
    return (
      <div className="field">
        <label className="field__label" htmlFor={`${id}-from`}>
          {filter.label}{filter.required ? <span className="field__req">Required</span> : null}
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <input id={`${id}-from`} className="control" type="date" value={value['from'] ?? ''}
            onChange={(e) => onChange({ from: e.currentTarget.value })} />
          <input className="control" type="date" value={value['to'] ?? ''}
            onChange={(e) => onChange({ to: e.currentTarget.value })} />
        </div>
        <div className="presets">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className="preset"
              onClick={() => onChange(preset.range())}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    )
  }
  if (filter.control === 'lookup' && filter.lookup) {
    return (
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {filter.label}{filter.required ? <span className="field__req">Required</span> : null}
        </label>
        <Lookup entity={filter.lookup.entity} value={value[filter.key] ?? ''}
          onChange={(v) => onChange({ [filter.key]: v })} placeholder="Start typing to search" />
      </div>
    )
  }
  if (filter.control === 'select') {
    return (
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {filter.label}{filter.required ? <span className="field__req">Required</span> : null}
        </label>
        <select id={id} className="control" value={value[filter.key] ?? ''}
          onChange={(e) => onChange({ [filter.key]: e.currentTarget.value })}>
          <option value="">Not chosen</option>
          {(filter.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    )
  }
  if (filter.control === 'checkbox') {
    return (
      <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 }}>
        <input type="checkbox" checked={value[filter.key] === 'true'}
          onChange={(e) => onChange({ [filter.key]: e.currentTarget.checked ? 'true' : '' })} />
        {filter.label}
      </label>
    )
  }
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{filter.label}</label>
      <input id={id} className="control" value={value[filter.key] ?? ''}
        onChange={(e) => onChange({ [filter.key]: e.currentTarget.value })} />
    </div>
  )
}

/**
 * The `DETAILS` modal (docs/10).
 *
 * A total on a report is only worth anything if it can be opened. This lists
 * the documents behind one row and links each of them, so an argument about a
 * number ends at a document rather than at a spreadsheet.
 */
const DetailsModal = ({
  companyId, reportKey, label, value, params, onClose,
}: {
  companyId: string
  reportKey: string
  label: string
  value: string
  params: Readonly<Record<string, string>>
  onClose: () => void
}) => {
  const api = useApi()
  const details = useQuery({
    queryKey: ['report-details', api.companyId, reportKey, value, params],
    queryFn: () => api.reportDetails(reportKey, value, params),
  })

  const columns: ColumnDef<DrillRow>[] = [
    {
      key: 'documentNo',
      header: 'Document',
      width: 180,
      render: (row) => (
        <Link to={paths.document(companyId, row.route, row.id)} className="mono">
          {row.documentNo ?? '(not numbered)'}
        </Link>
      ),
    },
    { key: 'docTypeLabel', header: 'Type', width: 180 },
    { key: 'documentDate', header: 'Date', width: 130, render: (r) => formatDate(r.documentDate) },
    { key: 'documentStatus', header: 'Status', type: 'status', width: 140 },
    { key: 'partyName', header: 'Party', width: 220, render: (r) => r.partyName ?? '—' },
    { key: 'itemName', header: 'Item', render: (r) => r.itemName ?? '—' },
    { key: 'quantity', header: 'Qty', align: 'right', width: 110, render: (r) => formatQuantity(r.quantity) },
    { key: 'amount', header: 'Amount', type: 'money', align: 'right', width: 150 },
  ]

  return (
    <Modal
      title={details.data?.label ?? label}
      wide
      footerLeft={details.data ? `${details.data.rows.length} document line${details.data.rows.length === 1 ? '' : 's'}` : ''}
      actions={<Button kind="ghost" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      {details.isLoading ? <Loading /> : null}
      {details.error ? <ErrorBlock error={details.error} /> : null}
      {details.data ? (
        <>
          <Table
            columns={columns}
            rows={[...details.data.rows]}
            getKey={(row) => `${row.id}:${row.itemName ?? ''}:${row.amount ?? ''}`}
          />
          <div className="report-footer">
            <span><strong>Total </strong><Money value={details.data.total} /></span>
          </div>
        </>
      ) : null}
    </Modal>
  )
}

/** Any report, drawn entirely from its definition. */
export const ReportPage = ({ companyId, reportRoute, query }: {
  companyId: string; reportRoute: string; query: Query
}) => {
  const api = useApi()
  const defs = useReportDefs()
  const def = defs.data?.find((d) => d.route === reportRoute)

  const [draft, setDraft] = useState<Record<string, string>>(query as Record<string, string>)
  const [applied, setApplied] = useState<Record<string, string> | null>(
    Object.keys(query).length > 0 ? (query as Record<string, string>) : null,
  )

  const defaults = useMemo(() => defaultFilterValues(def?.filters ?? []), [def])
  const seeded = useRef(false)
  useEffect(() => {
    // Runs once the definition arrives: a page opened without a query in the
    // address bar starts on the default period, and shows figures straight away
    // when that is all it was waiting for.
    if (seeded.current || !def || Object.keys(query).length > 0) return
    seeded.current = true
    if (Object.keys(defaults).length === 0) return
    setDraft((current) => ({ ...defaults, ...current }))
    if (answersRequired(def.filters, defaults)) setApplied(defaults)
  }, [def, defaults, query])

  const [view, setView] = useState<'summary' | 'detailed'>('summary')
  const [drill, setDrill] = useState<{ value: string; label: string } | null>(null)

  const params = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(applied ?? {})) if (v) out[k] = v
    if (def?.hasSummaryDetail) out['view'] = view
    return out
  }, [applied, view, def?.hasSummaryDetail])

  const chooser = useChosenColumns(def ? `report:${def.key}` : '', def?.columns ?? [])
  const run = useQuery({
    queryKey: ['report', api.companyId, def?.key, params],
    queryFn: () => api.runReport(def?.key ?? '', params),
    enabled: Boolean(def?.key) && applied !== null,
  })

  if (defs.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No report at '${reportRoute}'`)} /></div>

  const columns: ColumnDef<Record<string, unknown>>[] = chooser.visible.map((c) => ({
    key: c.key, header: c.label, width: c.width, align: c.align,
    render: (row: Record<string, unknown>) => cell(c, row[c.key]),
  }))

  const required = def.filters.filter((f) => f.required)

  return (
    <div className="page">
      <Link to={paths.hub(companyId, 'reports')} className="crumb"><Icon name="left" size={15} /> Reports</Link>
      <h1 className="page-title">{def.label}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      {def.caveat ? <div style={{ margin: '16px 0' }}><Notice kind="warn">{def.caveat}</Notice></div> : null}

      <Panel title="Filters" aside={required.length ? `${required.length} required` : undefined}>
        <div className="field-grid">
          {def.filters.map((f) => (
            <FilterControl key={f.key} filter={f} value={draft}
              onChange={(patch) => setDraft({ ...draft, ...patch })} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <Button kind="primary" onClick={() => {
            setApplied(draft)
            navigate(paths.report(companyId, reportRoute, draft))
          }}>Apply</Button>
          <Button kind="ghost" onClick={() => { setDraft({}); setApplied(null) }}>Clear</Button>
          <ColumnsButton all={def.columns} state={chooser} title="Customise the columns" />
          {/* The same saved-view machinery every list uses: a report anybody
              runs weekly is one nobody should have to re-filter weekly. */}
          <SavedViews
            scope={`report:${def.key}`}
            filters={draft}
            columns={chooser.chosen}
            onApply={(saved) => {
              setDraft({ ...saved.filters })
              setApplied({ ...saved.filters })
              if (saved.columns.length > 0) chooser.choose(saved.columns)
              navigate(paths.report(companyId, reportRoute, saved.filters))
            }}
          />
          <ExportButton
            href={api.exportUrl('reports', def.key, params, chooser.chosen)}
            disabled={applied === null || (run.data?.count ?? 0) === 0}
            count={run.data?.count ?? 0}
            what="rows"
          />
          {def.hasSummaryDetail ? (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 0 }}>
              {(['summary', 'detailed'] as const).map((v) => (
                <button key={v} type="button" className={view === v ? 'seg seg--on' : 'seg'}
                  onClick={() => setView(v)}>{v === 'summary' ? 'Summary' : 'Detailed'}</button>
              ))}
            </div>
          ) : null}
        </div>
      </Panel>

      <div style={{ marginTop: 24 }}>
        {applied === null ? (
          <Empty
            title="Set the filters, then apply"
            body={required.length
              ? `This report needs ${required.map((f) => f.label.toLowerCase()).join(' and ')} before it can run.`
              : 'Choose what you want to see and press Apply. Reports read across every document, so they are not run automatically.'}
          />
        ) : run.isLoading ? <Loading />
          : run.error ? <ErrorBlock error={run.error} />
          : !run.data || run.data.rows.length === 0 ? (
            <Empty title="No entries"
              body="Nothing matches those filters. Widen the dates, or clear a filter and try again." />
          ) : (
            <>
              <Table
                columns={columns}
                rows={[...run.data.rows]}
                getKey={(row: Record<string, unknown>) => JSON.stringify(row).slice(0, 120)}
                {...(def.drill
                  ? {
                    onRowClick: (row: Record<string, unknown>) => {
                      const value = row[def.drill!.key]
                      if (value === null || value === undefined || value === '') return
                      setDrill({
                        value: String(value),
                        label: def.drill!.label ?? 'Details',
                      })
                    },
                  }
                  : {})}
              />
              <div className="report-footer">
                <span><strong>Total entries: </strong><span className="num">{run.data.count}</span></span>
                <span style={{ display: 'flex', gap: 26 }}>
                  {def.columns.filter((c) => c.total).map((c) => (
                    <span key={c.key}>
                      <span style={{ color: 'var(--muted)' }}>{c.label} </span>
                      {c.type === 'qty'
                        ? <span className="num">{formatQuantity(run.data.totals[c.key] ?? '0')}</span>
                        : <Money value={run.data.totals[c.key] ?? '0'} />}
                    </span>
                  ))}
                </span>
              </div>
            </>
          )}
      </div>

      {drill ? (
        <DetailsModal
          companyId={companyId}
          reportKey={def.key}
          label={drill.label}
          value={drill.value}
          params={params}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </div>
  )
}
